import { InputDialog, QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { showErrorNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import { SidebarButton, SidebarItem } from '../../../widgets/layouts';
import { TreeSidebar } from '../../../widgets/sidebar/treeSideBar';
import React, { FC, useCallback, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { T, stringFormat } from 'src/shared/helpers';
import PencilIcon from 'src/shared/material_icons/image/edit.svg';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import AddIcon from 'src/shared/material_icons/content/add.svg';
import { Icon } from '../../../widgets/icon';
import styled from 'styled-components';
import {
    AIAssetLibrarySortBy,
    SortDirection,
    assetLibraryQueriesAIAssetLibraryCategoriesQuery,
    assetLibraryQueriesAIAssetLibraryCategoriesQuery$data
} from './__generated__/assetLibraryQueriesAIAssetLibraryCategoriesQuery.graphql';
import { assetLibraryQueriesDeleteAIAssetLibraryCategoryMutation } from './__generated__/assetLibraryQueriesDeleteAIAssetLibraryCategoryMutation.graphql';
import { assetLibraryQueriesUpdateAIAssetLibraryCategoryMutation } from './__generated__/assetLibraryQueriesUpdateAIAssetLibraryCategoryMutation.graphql';
import { aiAssetLibraryCategoriesQuery, deleteAIAssetLibraryCategoryMutation, updateAIAssetLibraryCategoryMutation } from './assetLibraryQueries';

type CategoryNode = NonNullable<NonNullable<assetLibraryQueriesAIAssetLibraryCategoriesQuery$data['aiAssetLibraryCategories']>['nodes']>[number];

const LegacyTreeContent = styled.div`
    padding: 10px;
`;
const LegacyCategoryItem = styled.div<{ $selected: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    margin-bottom: 5px;
    cursor: pointer;
    background: ${(props) => (props.$selected ? '#e5e7eb' : 'transparent')};
    border-radius: 8px;
    &:hover {
        background: #e5e7eb;
    }
`;
const LegacyCategoryName = styled.span<{ $selected: boolean }>`
    font-size: 14px;
    font-weight: ${(props) => (props.$selected ? 600 : 400)};
    color: ${(props) => props.theme.app.textColor};
    flex: 1;
`;
const LegacyCategoryActions = styled.div`
    display: flex;
    gap: 8px;
    opacity: 0;
    ${LegacyCategoryItem}:hover & {
        opacity: 1;
    }
`;
const LegacyActionIcon = styled(Icon)`
    cursor: pointer;
`;

interface AssetCategoriesTreeProps {
    selectedCategoryId: string | null;
    onCategorySelect: (categoryId: string | null) => void;
    searchName: string;
    sortBy: AIAssetLibrarySortBy;
    sortDirection: SortDirection;
    externalRefetchKey?: number;
    onAddCategory?: () => void;
    standalone?: boolean;
}

export const AssetCategoriesTree: FC<AssetCategoriesTreeProps> = ({
    selectedCategoryId,
    onCategorySelect,
    searchName,
    sortBy,
    sortDirection,
    externalRefetchKey = 0,
    onAddCategory,
    standalone = false
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [refetchKey, setRefetchKey] = useState(0);

    const categoriesData = useLazyLoadQuery<assetLibraryQueriesAIAssetLibraryCategoriesQuery>(
        aiAssetLibraryCategoriesQuery,
        {
            searchName: searchName || undefined,
            sortBy,
            sortDirection
        },
        { fetchKey: refetchKey + externalRefetchKey, fetchPolicy: 'store-and-network' }
    );

    const categories = categoriesData.aiAssetLibraryCategories?.nodes || [];

    const handleRefetch = useCallback(() => {
        setRefetchKey((key) => key + 1);
    }, []);

    const [updateCategory] = useMutation<assetLibraryQueriesUpdateAIAssetLibraryCategoryMutation>(updateAIAssetLibraryCategoryMutation);
    const [deleteCategory] = useMutation<assetLibraryQueriesDeleteAIAssetLibraryCategoryMutation>(deleteAIAssetLibraryCategoryMutation);

    const handleUpdateCategory = useCallback(
        (id: string, name: string, sortOrder: number) => {
            updateCategory({
                variables: {
                    id,
                    name: name.trim(),
                    sortOrder
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Category updated.', 'Admin') });
                    handleRefetch();
                },
                onError: (error) => {
                    console.error('Failed to update category:', error);
                    showErrorNotification({ message: T._('Failed updating category', 'Admin') });
                }
            });
        },
        [updateCategory, handleRefetch]
    );

    const handleDeleteCategory = useCallback(
        (id: string) => {
            deleteCategory({
                variables: {
                    id
                },
                onCompleted: () => {
                    showSuccessNotification({ message: T._('Category deleted.', 'Admin') });

                    if (selectedCategoryId === id) {
                        onCategorySelect(null);
                    }
                    handleRefetch();
                },
                onError: (error) => {
                    console.error('Failed to delete category:', error);
                    showErrorNotification({ message: T._('Failed deleting category', 'Admin') });
                }
            });
        },
        [selectedCategoryId, deleteCategory, onCategorySelect, handleRefetch]
    );

    const handleEditClick = useCallback(
        (category: CategoryNode) => {
            showDialog(
                'edit-category',
                <InputDialog
                    label={T._('Category name', 'Admin')}
                    defaultValue={category.name}
                    required
                    onConfirm={(name) => {
                        closeDialog('edit-category');
                        handleUpdateCategory(category.id, name, category.sortOrder);
                    }}
                />
            );
        },
        [showDialog, closeDialog, handleUpdateCategory]
    );

    const handleDeleteClick = useCallback(
        (category: CategoryNode) => {
            showDialog(
                'delete-category',
                <QuestionDialog
                    destructive
                    message={stringFormat(
                        T._('Do you want to remove the category "{0}"? Assets in this category will become uncategorized.', 'Admin'),
                        category.name
                    )}
                    onYesClick={() => {
                        closeDialog('delete-category');
                        handleDeleteCategory(category.id);
                    }}
                />
            );
        },
        [showDialog, closeDialog, handleDeleteCategory]
    );

    if (!standalone) {
        return (
            <LegacyTreeContent>
                <LegacyCategoryItem $selected={selectedCategoryId === null} onClick={() => onCategorySelect(null)}>
                    <LegacyCategoryName $selected={selectedCategoryId === null}>{T._('All Assets', 'Admin')}</LegacyCategoryName>
                </LegacyCategoryItem>
                {categories.map((category) => (
                    <LegacyCategoryItem key={category.id} $selected={selectedCategoryId === category.id} onClick={() => onCategorySelect(category.id)}>
                        <LegacyCategoryName $selected={selectedCategoryId === category.id}>{category.name}</LegacyCategoryName>
                        <LegacyCategoryActions>
                            <LegacyActionIcon
                                $hoverable
                                tooltip={T._('Edit', 'Admin')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleEditClick(category);
                                }}
                            >
                                <PencilIcon />
                            </LegacyActionIcon>
                            <LegacyActionIcon
                                $hoverable
                                tooltip={T._('Delete', 'Admin')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteClick(category);
                                }}
                            >
                                <TrashIcon />
                            </LegacyActionIcon>
                        </LegacyCategoryActions>
                    </LegacyCategoryItem>
                ))}
            </LegacyTreeContent>
        );
    }

    const actions: SidebarButton[] = [
        { key: 'edit', tooltip: T._('Edit category', 'Admin'), icon: <PencilIcon /> },
        { key: 'delete', tooltip: T._('Delete category', 'Admin'), icon: <TrashIcon /> }
    ];
    const sidebarItems: SidebarItem<string>[] = [
        { id: '', label: T._('All categories', 'Admin'), selected: selectedCategoryId === null },
        ...categories.map((category) => ({ id: category.id, label: category.name, selected: selectedCategoryId === category.id, buttons: actions }))
    ];

    return (
        <TreeSidebar
            sidebarHeader={T._('Categories', 'Admin')}
            sidebarAllowSearch
            sidebarHeaderButtons={onAddCategory ? [{ key: 'add', tooltip: T._('Add a new category', 'Admin'), icon: <AddIcon /> }] : undefined}
            sidebarItems={sidebarItems}
            onSidebarItemClick={(item) => onCategorySelect(item.id || null)}
            onSidebarHeaderButtonClick={() => onAddCategory?.()}
            onSidebarItemButtonClick={(button, item) => {
                const category = categories.find((entry) => entry.id === item.id);
                if (!category) return;
                if (button.key === 'edit') handleEditClick(category);
                if (button.key === 'delete') handleDeleteClick(category);
            }}
        />
    );
};
